/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ViewImageDialogBox.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { HandleApiActions } from "../../redux/actions";
import testCasesActions from "../../redux/actions/testCasesActions";

function ViewImageDialog({ open, onClose, fileName }) {
  const dispatch = useDispatch();
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchImage = async () => {
      if (!fileName) return;

      try {
        setLoading(true);

        const resp = await dispatch(
          HandleApiActions({
            ...testCasesActions.get_files,
            params: { filename: fileName },
            is_get_file: true,
          })
        );

        const url = window.URL.createObjectURL(new Blob([resp]));
        setImageUrl(url);
      } catch (error) {
        setImageUrl(null);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchImage();
    }

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [open, fileName]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">{fileName}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress />
          </Box>
        ) : imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={fileName}
            sx={{
              width: "100%",
              maxHeight: 600,
              objectFit: "contain",
              borderRadius: 2,
            }}
          />
        ) : (
          <Typography color="error">Unable to load image</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ViewImageDialog;